import type { AppliedPayeeValues } from '@/components/ReportForm/InstantPayeeSwitcher/hooks/useChangePayeeValue';
import { TOTAL_AMOUNT_TARGET_KEY } from '@/context/services/ap_mismatch_alert_settings/utils';
import { useGetApPayeeBankDetailsWithStatus } from '@/context/services/ap_payee/ApPayee.service';
import { useGetReportInferences } from '@/context/services/report_inferences/ReportInferences.service';
import {
  toComparableDueDate,
  type ApReportInference,
  type InferenceEntry,
} from '@/context/services/report_inferences/utils';
import { useOcrContext } from '@/features/InvoiceReport/Edit/components/Context/OcrContext';
import {
  useInferenceAlertState,
  usePayeeBankInferenceAlertState,
} from '@/features/InvoiceReport/Edit/components/hooks/useInferenceAlertState';
import { useInferenceBaseline } from '@/features/InvoiceReport/Edit/components/hooks/useInferenceBaseline';
import { type InferenceAlerts } from '@/features/InvoiceReport/Edit/components/ReportFormInputs/ReportFormInputs';
import { PaymentRequestForm } from '@/features/InvoiceReport/Edit/type';
import { useDataExtractionMismatchAlertFlag } from '@/hooks/feature_flag/useDataExtractionMismatchAlertFlag';
import { useDataExtractionMismatchAlertPhase2Flag } from '@/hooks/feature_flag/useDataExtractionMismatchAlertPhase2Flag';
import { useMismatchAlert } from '@/hooks/invoice_reports/useMismatchAlert';
import { useCallback, useEffect } from 'react';
import { useWatch, type Control, type UseFormGetValues } from 'react-hook-form';

type UseReportInferenceAlertsParams = {
  reportId: string;
  reportTypeId: string;
  control: Control<PaymentRequestForm>;
  getValues: UseFormGetValues<PaymentRequestForm>;
  payeeInputId?: string;
  dueDateInputId?: string;
  invoiceRegistrationNumberId?: string;
  isActiveInstantPayee?: boolean;
};

const NO_INFERENCES: ApReportInference[] = [];

export type UseReportInferenceAlertsReturn = {
  inferenceAlerts: InferenceAlerts;
  totalAmountInference: InferenceEntry | undefined;
  isTotalAmountAlertEnabled: boolean;
  applyPayeeDerivedBaseline: (applied?: AppliedPayeeValues) => void;
};

export const useReportInferenceAlerts = ({
  reportId,
  reportTypeId,
  control,
  getValues,
  payeeInputId,
  dueDateInputId,
  invoiceRegistrationNumberId,
  isActiveInstantPayee,
}: UseReportInferenceAlertsParams): UseReportInferenceAlertsReturn => {
  const { reflectedInferences } = useOcrContext();

  const isMismatchAlertFlag = useDataExtractionMismatchAlertFlag();
  const isMismatchAlertPhase2Flag = useDataExtractionMismatchAlertPhase2Flag();
  const rawReportInferences = useGetReportInferences(
    reportId,
    isMismatchAlertFlag
  );
  const inferences =
    reflectedInferences ?? rawReportInferences?.inferences ?? NO_INFERENCES;
  const { inferencesMap, isFieldAlertEnabled } = useMismatchAlert(
    inferences,
    reportTypeId
  );
  const { baseline, overrideBaselineEntry } =
    useInferenceBaseline(inferencesMap);

  /**
   * dueData that is derived from payee rule is treated as the baseline for subsequent comparisons.
   * If it is not derived (i.e. "payee only"), the previous baseline value is retained as-is.
   */
  const applyPayeeDerivedBaseline = useCallback(
    (applied?: AppliedPayeeValues) => {
      if (!isMismatchAlertPhase2Flag) return;
      const value = toComparableDueDate(applied?.dueDate);
      if (!value) return;
      overrideBaselineEntry('dueDate', {
        source: 'system',
        sourceDetail: 'payee',
        value,
      });
    },
    [isMismatchAlertPhase2Flag, overrideBaselineEntry]
  );

  const payeeFormValue = useWatch({ control, name: 'payee.value' });
  const selectedPayeeId = payeeFormValue || undefined;
  const isPayeeAlertEnabled =
    !!payeeInputId &&
    !isActiveInstantPayee &&
    isFieldAlertEnabled(payeeInputId);
  const shouldLookupPayeeBankDetails =
    isPayeeAlertEnabled && !!baseline.apPayee;

  const {
    accountNumber: selectedPayeeAccountNumber,
    bankName: selectedPayeeBankName,
    bankBranch: selectedPayeeBankBranch,
    isLoading: isSelectedPayeeBankDetailsLoading,
  } = useGetApPayeeBankDetailsWithStatus(
    shouldLookupPayeeBankDetails ? selectedPayeeId : undefined
  );

  const getPayeeCurrentValues = useCallback(
    () => ({
      accountNumber: selectedPayeeAccountNumber,
      bankName: selectedPayeeBankName,
      bankBranch: selectedPayeeBankBranch,
    }),
    [selectedPayeeAccountNumber, selectedPayeeBankName, selectedPayeeBankBranch]
  );
  const getDueDateComparableValue = useCallback(
    () => toComparableDueDate(getValues('payee.dueDate')),
    [getValues]
  );
  const getInvoiceRegNumComparableValue = useCallback(() => {
    if (!invoiceRegistrationNumberId) return null;
    const value = getValues('reportForm')?.[invoiceRegistrationNumberId];
    return typeof value === 'string' ? value : null;
  }, [getValues, invoiceRegistrationNumberId]);

  const payee = usePayeeBankInferenceAlertState(
    {
      bankName: baseline.payeeBankName,
      bankBranch: baseline.payeeBankBranch,
      accountNumber: baseline.apPayee,
    },
    getPayeeCurrentValues,
    isPayeeAlertEnabled && !isSelectedPayeeBankDetailsLoading
  );

  const { onBlur: recomputePayeeAlert } = payee;
  useEffect(() => {
    recomputePayeeAlert();
  }, [
    recomputePayeeAlert,
    selectedPayeeAccountNumber,
    selectedPayeeBankName,
    selectedPayeeBankBranch,
    isSelectedPayeeBankDetailsLoading,
  ]);

  const dueDate = useInferenceAlertState(
    baseline.dueDate,
    getDueDateComparableValue,
    !!dueDateInputId && isFieldAlertEnabled(dueDateInputId)
  );
  const invoiceRegistrationNumber = useInferenceAlertState(
    baseline.invoiceRegistrationNumber,
    getInvoiceRegNumComparableValue,
    !!invoiceRegistrationNumberId &&
      isFieldAlertEnabled(invoiceRegistrationNumberId)
  );

  return {
    inferenceAlerts: { payee, dueDate, invoiceRegistrationNumber },
    totalAmountInference: baseline.totalAmount,
    isTotalAmountAlertEnabled: isFieldAlertEnabled(TOTAL_AMOUNT_TARGET_KEY),
    applyPayeeDerivedBaseline,
  };
};
